<template>
  <div id="app">
    <LoadingBar />
    <invalid-integration
      v-if="isNotConfigured"
      class="p-4"
      @logout="logout"
      @reload="initialize"
    />
    <template v-else-if="isValid">
      <app-tabs
        v-show="!selectedBookable && !selectedServiceOrder && !selectedVisitor"
        class="h-full p-4"
        :tabs="tabs"
      >
        <template #room-finder>
          <div v-if="showAttendees">
            <h2 class="text-base font-medium mb-3">
              {{ $tc('Rooms.SelectedRooms', attendeesBookables.length) }}
            </h2>
            <div class="flex-col gap-3 flex">
              <Card
                v-for="bookable of attendeesBookables"
                :key="bookable.id + 'card'"
                :action="{
                  label: $t('App.RemoveRoom'),
                  danger: true,
                  icon: Trash2,
                  onClick: () => removeAttendeeByBookable(bookable)
                }"
                :subtitle="bookable.integration_email"
                selected
                @open="selectBookable(bookable)"
              >
                <template #title>
                  {{ bookable.name }}
                  <FavouriteButton
                    :id="bookable.id"
                    type="bookable"
                  />
                </template>
                <template #details>
                  <BookableStatus
                    :booked="!!bookable.bookings.length"
                    :booked-by-current-event="bookable.bookings.some(booking => booking.event_id === flexopusEventId)"
                  />
                  {{ bookable.location?.building?.name }},
                  {{ bookable.location?.name }}
                </template>
              </Card>
            </div>
            <div class="divider mt-4" />
          </div>
          <div class="h-full mt-4">
            <bookable-list
              :attendees="attendees"
              :available-equipment="availableEquipment"
              :bookables="availableBookables"
              :buildings="availableBuildings"
              :locations="availableLocations"
              :loading="loading"
              :params="params"
              :flexopus-event-id="flexopusEventId"
              @select-bookable="selectBookable"
              @search="search"
            />
          </div>
        </template>
        <template #services>
          <div
            v-if="recurrence"
            class="card flex gap-2"
          >
            <AlertTriangle class="m-1 w-5 h-5 text-neutral-500 shrink-0" />
            <div>
              <h4 class="text-flexopus-black font-bold text-base leading-normal">
                {{ $t('Services.RecurringError.Title') }}
              </h4>
              <h6 class="text-neutral-gray-700 text-sm leading-normal">
                {{ $t('Services.RecurringError.Description') }}
              </h6>
            </div>
          </div>
          <template v-else-if="!flexopusEvent">
            <div class="card flex gap-2 mb-3">
              <AlertTriangle class="m-1 w-5 h-5 text-neutral-500 shrink-0" />
              <div>
                <h4 class="text-flexopus-black font-bold text-base leading-normal">
                  {{ $t('Services.SyncError.Title') }}
                </h4>
                <h6 class="text-neutral-gray-700 text-sm leading-normal">
                  {{ $t('Services.SyncError.Description') }}
                </h6>
              </div>
            </div>
            <AsyncButton
              class="btn btn-secondary w-full"
              @click="fetchEventWithFeedBack"
            >
              <RotateCcw class="w-5 h-5 mr-2" /> {{ $t('Services.Refresh') }}
            </AsyncButton>
          </template>
          <template v-else>
            <ServiceOrderList
              v-if="serviceOrders.length"
              :service-orders="serviceOrders"
              @delete="fetchEvent"
              @edit="onServiceOrderEdit"
            />
            <ServiceOrderCreate
              :bookables="attendeesBookables"
              @create="onServiceOrderCreate"
            />
          </template>
        </template>
        <template #visitor-invitations>
          <div
            v-if="recurrence"
            class="card flex gap-2"
          >
            <AlertTriangle class="m-1 w-5 h-5 text-neutral-500 shrink-0" />
            <div>
              <h4 class="text-flexopus-black font-bold text-base leading-normal">
                {{ $t('Visitors.RecurringError.Title') }}
              </h4>
              <h6 class="text-neutral-gray-700 text-sm leading-normal">
                {{ $t('Visitors.RecurringError.Description') }}
              </h6>
            </div>
          </div>
          <template v-else-if="!flexopusEvent">
            <div class="card flex gap-2 mb-3">
              <AlertTriangle class="m-1 w-5 h-5 text-neutral-500 shrink-0" />
              <div>
                <h4 class="text-flexopus-black font-bold text-base leading-normal">
                  {{ $t('Services.SyncError.Title') }}
                </h4>
                <h6 class="text-neutral-gray-700 text-sm leading-normal">
                  {{ $t('Services.SyncError.Description') }}
                </h6>
              </div>
            </div>
            <AsyncButton
              class="btn btn-secondary w-full"
              @click="fetchEventWithFeedBack"
            >
              <RotateCcw class="w-5 h-5 mr-2" /> {{ $t('Services.Refresh') }}
            </AsyncButton>
          </template>
          <template v-else>
            <VisitorList
              :visitors="visitors"
              :settings="settings"
              :flexopus-event-id="flexopusEventId"
              :visitor-building-check="visitorBuildingCheck"
              @details="selectedVisitor = $event"
              @update="fetchEvent"
            />
          </template>
        </template>
        <template #information>
          <configuration-info
            :user-name="user.name"
            :user-email="user.email"
            :tenant="tenant"
            :version="version"
            @logout="logout"
          />
        </template>
      </app-tabs>
      <div
        v-if="selectedBookable"
        class="p-4"
      >
        <BackHeader
          class="mb-4"
          @click="selectedBookable = null"
        />

        <div class="flex flex-col">
          <h3
            class="text-lg font-medium"
          >
            {{ selectedBookable.name }}
          </h3>
          <h5 class="text-xs font-medium text-microsoft-blue-default pb-1">
            {{ selectedBookable.integration_email }}
          </h5>
          <div class="leading-none">
            <bookable-status
              :booked="!!selectedBookable.bookings.length"
              :booked-by-current-event="selectedBookable.bookings.some(booking => booking.event_id === flexopusEvent.id)"
            />
          </div>
        </div>
        <div class="divider my-4" />
        <div class="h-full mt-4">
          <bookable-details
            :bookable="selectedBookable"
            :is-selected-attendee="isSelectedAttendee"
            @add="addAttendee"
            @remove="removeAttendee"
          />
        </div>
      </div>
      <div
        v-if="selectedServiceOrder"
        class="p-4"
      >
        <BackHeader
          class="mb-4"
          @click="selectedServiceOrder = null"
        />
        <ServiceOrderEdit
          :service-order="selectedServiceOrder"
          :flexopus-event="flexopusEvent"
          :settings="settings"
          :cost-centers="costCenters"
          @submit="onOrderChanged"
          @cancel="selectedServiceOrder = null"
        />
      </div>
      <div
        v-if="selectedVisitor"
        class="p-4"
      >
        <BackHeader
          class="mb-4"
          @click="selectedVisitor = null"
        />
        <VisitorDetails
          :visitor="selectedVisitor"
          :settings="settings"
          :flexopus-event-id="flexopusEventId"
          @update="onVisitorChanged"
          @cancel="selectedVisitor = null"
        />
      </div>
    </template>
    <app-loading
      v-else-if="isLoading"
      :loading="$t('App.Loading')"
      class="flex flex-col h-screen w-screen items-center justify-center p-4"
    />
    <login-on-plugin
      v-else
      class="p-4"
      @login="login"
    />
  </div>
</template>

<script>
import AppLoading from '@/components/AppLoading.vue'
import AppTabs from '@/components/AppTabs.vue'
import BookableDetails from '@/components/BookableDetails.vue'
import BookableList from '@/components/BookableList.vue'
import BookableStatus from '@/components/BookableStatus.vue'
import ConfigurationInfo from '@/components/ConfigurationInfo.vue'
import InvalidIntegration from '@/components/InvalidIntegration.vue'
import LoginOnPlugin from '@/components/LoginOnPlugin.vue'
import InitLanguage from '@/mixins/init-language'
import outlook from '@/outlook'
import axios from 'axios'
import { AlertTriangle, Coffee, IdCard, RotateCcw, Search, Settings, Trash2 } from 'lucide-vue'
import { computed, ref } from 'vue'
import ServiceOrderCreate from '@/components/ServiceOrderCreate.vue'
import api from '@/lib/api'
import ServiceOrderEdit from '@/components/ServiceOrderEdit.vue'
import ServiceOrderList from '@/components/ServiceOrderList.vue'
import { useNotify } from './composables/notify'
import { useConfirmDialog } from './composables/confirmDialog'
import AsyncButton from './components/AsyncButton.vue'
import LoadingBar from './components/LoadingBar.vue'
import { useLoadingBar } from './composables/loadingBar'
import VisitorList from './components/VisitorList.vue'
import VisitorDetails from './components/VisitorDetails.vue'
import Card from './components/Card.vue'
import BackHeader from './components/BackHeader.vue'
import FavouriteButton from './components/FavouriteButton.vue'
import { useFavourites } from './composables/favourites'
import uniqBy from 'lodash/uniqBy'
import flatMap from 'lodash/flatMap'

const ConfigStates = {
  LOADING: 'loading',
  NOT_CONFIGURED: 'not_configured',
  NOT_VALID: 'not_valid',
  VALID: 'valid',
}

export default {
  components: {
    BookableStatus,
    AppLoading,
    AppTabs,
    BookableDetails,
    BookableList,
    ConfigurationInfo,
    InvalidIntegration,
    LoginOnPlugin,
    ServiceOrderCreate,
    ServiceOrderEdit,
    ServiceOrderList,
    AlertTriangle,
    RotateCcw,
    AsyncButton,
    LoadingBar,
    VisitorList,
    VisitorDetails,
    Card,
    BackHeader,
    FavouriteButton,
  },
  mixins: [InitLanguage],
  setup () {
    const { start, end, recurrence, isAllDay } = outlook.useAppointmentSchedule()

    const params = ref({})
    const timeParams = computed(() => {
      if (start.value && end.value) {
        return { start: start.value, end: end.value }
      }
      return null
    })
    const searchParams = computed(() => {
      if (start.value && end.value) {
        return {
          ...params.value,
          start: start.value,
          end: end.value,
          recurrence: recurrence.value,
        }
      }
      return null
    })

    return {
      outlook,
      start,
      end,
      attendees: outlook.useRequiredAttendees(),
      eventLocations: outlook.useLocations(),
      params,
      searchParams,
      timeParams,
      recurrence,
      isAllDay,
      ...useNotify(),
      ...useConfirmDialog(),
      ...useLoadingBar(),
      ...useFavourites(),
    }
  },
  data () {
    return {
      tenant: null,
      validConfig: ConfigStates.LOADING,
      buildings: [],
      locations: [],
      equipment: [],
      costCenters: [],
      flexopusEvent: null,
      flexopusEventId: null,
      loading: false,
      bookables: [],
      initialBookables: [],
      dialog: null,
      selectedBookable: null,
      selectedServiceOrder: null,
      selectedVisitor: null,
      user: {
        name: null,
        email: null,
      },
      settings: {
        service_providers_enabled: false,
        service_provider_has_end_time: false,
        service_provider_cost_centers_enabled: false,
        service_provider_default_cost_centers_allowed: false,
        visitor_management_enabled: false,
        visitor_user_checkin: false,
      },
      Trash2,
    }
  },
  computed: {
    tabs () {
      const hasServiceProviders = this.settings.service_providers_enabled &&
        (
          this.serviceOrders.length ||
          this.initialBookables.some(
            bookable => this.buildings.some(
              building => building.service_providers?.length && building.id === this.locations.find(l => l.id === bookable.location_id)?.building_id,
            ),
          )
        )

      let serviceOrdersBadge
      if (this.flexopusEvent && this.serviceOrders.length) {
        serviceOrdersBadge = {
          text: this.serviceOrders.length,
          type: 'info',
        }
      }

      const invitationCount = this.visitors.filter(({ invitation }) => !!invitation).length
      let visitorsBadge
      if (!this.visitorBuildingCheck) {
        visitorsBadge = {
          text: '!',
          type: 'warning',
        }
      } else if (invitationCount) {
        visitorsBadge = {
          text: invitationCount,
          type: 'info',
        }
      }

      return [
        {
          slot: 'room-finder',
          name: this.$t('Tabs.RoomFinder'),
          icon: Search,
          badge: this.attendeesBookables.length
            ? {
                text: this.attendeesBookables.length,
                type: 'info',
              }
            : undefined,
        },
        ...(hasServiceProviders
          ? [
              {
                slot: 'services',
                name: this.$t('Tabs.Services'),
                icon: Coffee,
                disabled: !this.flexopusEvent || this.recurrence,
                badge: serviceOrdersBadge,
              },
            ]
          : []),
        ...(this.settings.visitor_management_enabled
          ? [
              {
                slot: 'visitor-invitations',
                name: 'Visitors',
                icon: IdCard,
                disabled: !this.flexopusEvent || this.recurrence,
                badge: visitorsBadge,
              },
            ]
          : []),
        {
          slot: 'information',
          name: this.$t('Tabs.Information'),
          icon: Settings,
        },
      ]
    },
    isSelectedAttendee () {
      return this.attendees.some(i => i === this.selectedBookable.integration_email)
    },
    configs () {
      return ConfigStates
    },
    isValid () {
      return ConfigStates.VALID === this.validConfig
    },
    isInvalid () {
      return ConfigStates.NOT_VALID === this.validConfig
    },
    isLoading () {
      return ConfigStates.LOADING === this.validConfig
    },
    isNotConfigured () {
      return ConfigStates.NOT_CONFIGURED === this.validConfig
    },
    attendeesBookables () {
      return this.initialBookables.filter(
        bookable => this.attendees.includes(bookable.integration_email) ||
          this.eventLocations.includes(bookable.integration_email),
      )
    },
    availableBookables () {
      return this.bookables.filter(
        bookable => !this.attendees.includes(bookable.integration_email) &&
          !this.eventLocations.includes(bookable.integration_email),
      ).sort((a, b) => {
        const aFav = this.favourites.bookable?.some(f => f.id === a.id) ? 1 : 0
        const bFav = this.favourites.bookable?.some(f => f.id === b.id) ? 1 : 0
        return bFav - aFav
      })
    },
    version () {
      return process.env.VUE_APP_MSOPLUGIN_VERSION ?? 'dev'
    },
    showAttendees () {
      return this.attendeesBookables.length > 0 && !this.selectedBookable
    },
    availableEquipment () {
      return this.equipment.filter(e => this.initialBookables.some(b => b.equipments.includes(e.name)))
    },
    availableLocations () {
      return this.locations.filter(l => this.initialBookables.some(b => b.location_id === l.id))
    },
    availableBuildings () {
      return this.buildings.filter(b => this.availableLocations.some(l => l.building_id === b.id))
    },
    serviceOrders () {
      return this.flexopusEvent?.service_orders?.filter(item => this.attendeesBookables.find(bookable => bookable.id === item.bookable.id)) || []
    },
    visitors () {
      return this.flexopusEvent?.visitors?.filter(({ email }) => this.attendees.includes(email)) || []
    },
    visitorBuildingCheck () {
      if (!this.initialBookables.length) {
        // not loaded yet
        return true
      }
      const buildings = uniqBy(flatMap(this.attendeesBookables, 'location.building'), 'id')
      if (buildings.length !== 1 || !buildings.at(0)?.visitor_management) {
        return false
      }
      return true
    },
  },
  watch: {
    async timeParams () {
      if (this.validConfig === ConfigStates.VALID) {
        this.initialBookables = await this.getBookables(this.timeParams)
      }
    },
    async searchParams () {
      if (this.validConfig === ConfigStates.VALID) {
        this.bookables = await this.getBookables(this.searchParams)
        this.loading = false
      }
    },
    async recurrence (current, old) {
      if ((!!current) !== (!!old)) {
        this.bookables = await this.getBookables(this.searchParams)
      }
    },
  },
  async mounted () {
    this.initLanguage()
    await this.initialize()
  },
  methods: {
    async loadFlexopusEventId () {
      this.flexopusEventId = this.flexopusEventId || this.flexopusEvent?.id || await outlook.getCustomProperty('flexopusEventId')
    },
    async getBookables (filters) {
      const bookables = await api.getBookables(filters)
      return bookables.map(bookable => {
        const location = this.locations.find(l => l.id === bookable.location_id)
        return {
          ...bookable,
          location: location
            ? {
                ...location,
                building: this.buildings.find(b => b.id === location.building_id),
              }
            : null,
        }
      })
    },
    async initialize () {
      this.validConfig = ConfigStates.LOADING
      this.tenant = outlook.getSetting('endpoint')
      const token = outlook.getSetting('token')

      if (this.tenant && token) {
        try {
          const data = await api.initialize()
          if (!data.msgraph_configured) {
            this.validConfig = ConfigStates.NOT_CONFIGURED
            return
          }

          this.locations = data.locations
          this.buildings = data.buildings
          this.equipment = data.equipment
          this.costCenters = data.cost_centers
          this.settings = data.settings
          this.user.name = data.user.name
          this.user.email = data.user.email
          this.validConfig = ConfigStates.VALID
          this.initialBookables = await this.getBookables(this.timeParams)
          this.search({})
        } catch (error) {
          this.validConfig = ConfigStates.NOT_VALID
        }
      } else {
        this.validConfig = ConfigStates.NOT_VALID
      }
      await this.fetchEvent()
      await this.checkAccounts()
    },
    selectBookable (bookable) {
      this.selectedBookable = bookable
    },
    search (params) {
      this.loading = true
      this.params = params
    },
    login () {
      outlook.displayDialog(process.env.VUE_APP_MSOPLUGIN_URL + 'login.html', (message, dialog) => {
        axios.post(decodeURIComponent(message)).then(({ data }) => {
          outlook.setSetting('endpoint', data.endpoint)
          outlook.setSetting('token', data.token)
          outlook.saveSettings().then(() => this.initialize())
        })

        dialog.close()
      })
    },
    async logout () {
      if (!await this.confirm({
        text: this.$t('Logout.ConfirmText'),
      })) {
        return
      }
      await api.logout()
      outlook.setSetting('endpoint', null)
      outlook.setSetting('token', null)
      await outlook.saveSettings()
      this.validConfig = ConfigStates.NOT_VALID
    },
    addAttendee () {
      outlook.addAttendee(this.selectedBookable.integration_email)
      this.selectedBookable = null
    },
    removeAttendeeByBookable (bookable) {
      outlook.removeAttendee(bookable.integration_email)
      outlook.removeLocation(bookable.integration_email)
    },
    removeAttendee () {
      this.removeAttendeeByBookable(this.selectedBookable)
      this.selectedBookable = null
    },
    onServiceOrderCreate (order) {
      this.selectedServiceOrder = order
    },
    onServiceOrderEdit (order) {
      this.selectedServiceOrder = order
    },
    async onOrderChanged () {
      await this.fetchEvent()
      this.selectedServiceOrder = null
    },
    async onVisitorChanged () {
      await this.fetchEvent()
      this.$nextTick(() => {
        this.selectedVisitor = this.visitors.find(({ id }) => id === this.selectedVisitor.id)
      })
    },
    async fetchEventWithFeedBack () {
      try {
        await this.fetchEvent()
      } catch (error) {
        this.error(this.$t('App.Errors.EventNotFound'))
      }
    },
    async fetchEvent () {
      if (this.validConfig !== ConfigStates.VALID) {
        return
      }
      try {
        if (!(await outlook.getItemId())) {
          // item is not saved yet
          return
        }
      } catch (error) {
        // getItemId is not supported
        console.warn(error)
      }

      /**
       * @note Comment out this line to force "guessing mode".
       * Useful for testing scenarios where access to user calendar is not granted.
       */
      await this.loadFlexopusEventId()

      if (!this.attendeesBookables.length && !this.flexopusEventId) {
        return
      }
      await this.withLoadingBar(async () => {
        try {
          if (this.flexopusEventId) {
            this.flexopusEvent = await api.getEvent(this.flexopusEventId)
          } else {
          // cannot find event id in custom props, fallback to "guessing mode"
            this.flexopusEvent = await api.findEvent({
              from_time: this.start,
              to_time: this.end,
              all_day: this.isAllDay,
              bookables: this.attendeesBookables.map(bookable => bookable.id),
            })
          }
          await this.loadFlexopusEventId()
          await outlook.setCustomProperty('flexopusEventId', this.flexopusEvent.id)
        } catch (error) {
          console.error(error)
        }
      })
    },
    async checkAccounts () {
      if (
        this.validConfig === ConfigStates.VALID &&
        outlook.getUserProfile() &&
        outlook.getUserProfile().emailAddress !== this.user.email
      ) {
        if (await this.confirm({
          title: this.$t('App.Errors.AccountMismatchTitle'),
          text: this.$t('App.Errors.AccountMismatchText', {
            outlookEmail: outlook.getUserProfile().emailAddress,
            flexopusEmail: this.user.email,
          }),
          danger: true,
          confirmText: this.$t('App.Errors.AccountMismatchConfirm'),
        })) {
          this.logout()
        }
      }
    },
  },
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
